import { Text } from "@mdotm/shadcn/legacy/components";
import type { CustomAttributeValue, InvestmentSummary } from "@eu.mdotm.api/sphere";
import type { PrintableProps } from "$root/components/EvolvedPrint/configuration/types";
import { Card } from "$root/components/EvolvedPrint/components/Card";
import type { ReportTemplateItemMap } from "$root/functional-areas/reports/report-latest";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useReportTranslation } from "../translation";
import type { CustomAttributeDto } from "$root/functional-areas/customAttributes/type";
import { ForEach } from "@mdotm/utils/react-extra";
import { PortfolioCustomAttribute } from "./PortfolioCustomAttribute";
import { Map } from "immutable";
import { useMemo } from "react";

export function Summary(
	props: PrintableProps<
		{
			portfolio: InvestmentSummary;
			portfolioAttributes: CustomAttributeValue[] | undefined;
			availableAttributes: CustomAttributeDto[] | undefined;
			config: ReportTemplateItemMap["summary"];
		},
		unknown
	>,
): React.JSX.Element {
	const {
		name,
		primaryBenchmarkName,
		primaryBenchmarkType,
		primaryBenchmarkIdentifier,
		universeName,
		baseCurrency,
		referenceName,
		reference,
		// reference,
	} = props.portfolio;
	const { t } = useReportTranslation();
	const { availableAttributes, portfolioAttributes } = props;
	const availableAttributesMap = useMemo(
		() => Map(props.availableAttributes?.map((available) => [available.attributeUuid!, available])),
		[props.availableAttributes],
	);

	return (
		<Card>
			<div className="py-4 border-t border-b-2 pr-9" style={{ borderColor: themeCSSVars.palette_N300 }}>
				<div className="grid grid-cols-2 gap-x-8 gap-y-5">
					{props.config.standardFields.portfolioName && (
						<div>
							<Text type="Body/S/Light-UPPERCASE" color="rgb(140, 142, 168)">
								{t("REPORT_BUILDER.SUMMARY.PORTFOLIO_NAME")}
							</Text>
							<Text
								type="Body/S/Bold"
								as="div"
								className="max-w-[200px] truncate"
								data-title={name ?? "-"}
								title={name ?? "-"}
							>
								{name ?? "-"}
							</Text>
						</div>
					)}
					{props.config.standardFields.baseCurrency && (
						<div>
							<Text type="Body/S/Light-UPPERCASE" color="rgb(140, 142, 168)">
								{t("REPORT_BUILDER.SUMMARY.BASE_CURRENCY")}
							</Text>
							<Text
								type="Body/S/Bold"
								as="div"
								className="max-w-[200px] truncate"
								data-title={name ?? "-"}
								title={name ?? "-"}
							>
								{baseCurrency ?? "-"}
							</Text>
						</div>
					)}
					{props.config.standardFields.benchmark && (
						<div>
							<Text type="Body/S/Light-UPPERCASE" color="rgb(140, 142, 168)">
								{t("REPORT_BUILDER.SUMMARY.BENCHMARK")}
							</Text>
							<Text
								type="Body/S/Bold"
								as="div"
								data-title={t("REPORT_BUILDER.SUMMARY.BENCHMARK")}
								title={t("REPORT_BUILDER.SUMMARY.BENCHMARK")}
								className="whitespace-nowrap text-ellipsis overflow-hidden  cursor-pointer"
							>
								{primaryBenchmarkIdentifier ? primaryBenchmarkName : ""}
							</Text>
						</div>
					)}

					{props.config.standardFields.universe && (
						<div>
							<Text type="Body/S/Light-UPPERCASE" color="rgb(140, 142, 168)">
								{t("REPORT_BUILDER.SUMMARY.UNIVERSE")}
							</Text>
							<Text
								type="Body/S/Bold"
								as="div"
								data-title={primaryBenchmarkType ?? "-"}
								title={primaryBenchmarkType ?? "-"}
								className={{
									"whitespace-nowrap text-ellipsis overflow-hidden pr-4 underline cursor-pointer font-bold!":
										Boolean(universeName),
								}}
							>
								{universeName ?? t("REPORT_BUILDER.SUMMARY.NO_UNIVERSE")}
							</Text>
						</div>
					)}

					{props.config.standardFields.reference && (
						<div>
							<Text type="Body/S/Light-UPPERCASE" color="rgb(140, 142, 168)">
								{t("REPORT_BUILDER.SUMMARY.REFERENCE")}
							</Text>
							<Text
								type="Body/S/Bold"
								as="div"
								data-title={t("INVESTMENT_SUMMARY.REFERENCE")}
								title={t("REPORT_BUILDER.SUMMARY.REFERENCE")}
								className={{
									"whitespace-nowrap text-ellipsis overflow-hidden pr-4 underline cursor-pointer font-bold!": reference,
								}}
							>
								{referenceName || t("REPORT_BUILDER.SUMMARY.NO_REFERENCE")}
							</Text>
						</div>
					)}

					{availableAttributes != null && props.config.customAttributes != null && (
						<ForEach
							collection={props.config.customAttributes}
							keyProvider={({ uuid, enabled }, index) => `${uuid}-${enabled}-${index}`}
						>
							{({ enabled, uuid }) => {
								if (!enabled || !availableAttributesMap.has(uuid)) {
									return null;
								}

								const customAttributeRelativeToInvestment = portfolioAttributes?.find(
									(attr) => attr.attributeUuid === uuid,
								);

								return (
									<div>
										<Text type="Body/S/Light-UPPERCASE" color="rgb(140, 142, 168)">
											{availableAttributesMap.get(uuid)?.name}
										</Text>
										<PortfolioCustomAttribute
											availableAttributes={availableAttributes}
											customAttributeRelativeToInvestment={customAttributeRelativeToInvestment}
											textWrapper={(str) => (
												<Text
													type="Body/S/Bold"
													as="div"
													className="truncate"
													data-title={String(str ?? "-")}
													title={String(str ?? "-")}
												>
													{str}
												</Text>
											)}
										/>
									</div>
								);
							}}
						</ForEach>
					)}
				</div>
			</div>
		</Card>
	);
}
